// HighCommerce A/B price-test contract consumer (Level 2 integration).
//
// ab.js (snippets/hc-ab.liquid) publishes window.__HCAB.price — per tested
// variant {a, b} decimal strings for a visitor in the challenger arm, null
// for everyone else — and fires `hcab:prices` once the map has loaded. Money
// is always right (checkout reprices server-side); this module only keeps the
// DISPLAY honest: every Lettoria surface that renders or computes a price in
// JavaScript routes the catalog amount through testedPrice(), and every
// server-rendered price node opts in through one of three classes so the
// challenger arm never sees a figure derived from the control price.
//
//   .hcab-price     data-variant-id data-catalog        → tested catalog price
//   .hcab-derived   + data-pct data-fixed               → campaign "with code" price
//                                                         computed off the tested catalog
//   .hcab-save-pct  + data-compare                      → "SAVE n%" chip vs compare-at
//
// Rewrites read only the data attributes (never the displayed text), so a
// pass is idempotent and can run before or after ab.js's own selector
// rewrite without either fighting the other. `null` from the contract means
// "render the catalog price" — control visitors, untested variants and the
// moment before the map loads all look the same to callers.

const NODE_SELECTOR = '.hcab-price, .hcab-derived, .hcab-save-pct';

/** Tested price in cents for a variant, or the catalog cents when untested. */
export function testedPrice(variantId, catalogCents) {
  try {
    const entry = window.__HCAB?.price?.get?.(String(variantId));
    if (!entry || entry.b == null) return catalogCents;
    const cents = Math.round(parseFloat(entry.b) * 100);
    return Number.isFinite(cents) ? cents : catalogCents;
  } catch {
    return catalogCents;
  }
}

/**
 * Campaign "with code" price — mirrors snippets/campaign-price.liquid: a
 * fixed whole-dollar amount wins over a percent, clamped at zero.
 */
export function campaignPrice(cents, percent = 0, fixed = 0) {
  if (fixed > 0) return Math.max(0, cents - fixed * 100);
  if (percent > 0) return Math.round((cents * (100 - percent)) / 100);
  return cents;
}

/** Shop-currency money string, matching the `money` filter output. */
export const money = (cents) => `$${(cents / 100).toFixed(2)}`;

const dataInt = (el, key) => {
  const n = parseInt(el.dataset[key] ?? '', 10);
  return Number.isFinite(n) ? n : null;
};

/** True once ab.js has published a price map for this visitor. */
export function pricesReady() {
  try {
    return !!window.__HCAB?.price?.map?.();
  } catch {
    return false;
  }
}

/** Fires `handler` every time the price map (re)loads; returns an unsubscribe. */
export function onTestedPrices(handler) {
  document.addEventListener('hcab:prices', handler);
  return () => document.removeEventListener('hcab:prices', handler);
}

/** Re-prices every opted-in node under `root` (root itself included). */
export function repriceTested(root = document) {
  if (!root?.querySelectorAll) return;
  const nodes = Array.from(root.querySelectorAll(NODE_SELECTOR));
  if (root.matches?.(NODE_SELECTOR)) nodes.unshift(root);

  for (const el of nodes) {
    const variantId = el.dataset.variantId;
    const catalog = dataInt(el, 'catalog');
    if (!variantId || catalog === null) continue;
    const tested = testedPrice(variantId, catalog);

    if (el.classList.contains('hcab-save-pct')) {
      // Same integer maths as price-with-code.liquid; chip suppressed under 5%.
      const compare = dataInt(el, 'compare') ?? 0;
      const pct = compare > tested ? Math.floor(((compare - tested) * 100) / compare) : 0;
      const show = pct >= 5;
      el.hidden = !show;
      el.style.display = show ? '' : 'none';
      if (show) el.textContent = `SAVE ${pct}%`;
      continue;
    }

    const cents = el.classList.contains('hcab-derived')
      ? campaignPrice(tested, dataInt(el, 'pct') ?? 0, dataInt(el, 'fixed') ?? 0)
      : tested;
    const text = money(cents);
    if (el.textContent !== text) el.textContent = text;
  }
}

// Global pass: the module is loaded once from snippets/scripts.liquid so
// static surfaces (rails, catalogue cards, mega menu) are covered without
// their own script; components that morph or re-render call repriceTested()
// on their own subtree afterwards.
if (!window.__lettoriaTestedPriceBound) {
  window.__lettoriaTestedPriceBound = true;
  onTestedPrices(() => repriceTested(document));
  if (pricesReady()) repriceTested(document);
}
